import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import ArrowLeft from "../static/img/backArrow.png";
import styles from '../static/styles/GameChallenges.module.css';
import { fetchGameChallenges } from '../api/challenges';
import { submitGameEntry } from '../api/games';
import GamingTaskSubmitPopup from '../components/GamingTaskSubmitPopup';
import aimTrainLevels from '../static/data/aimTrainLevels.json';
import puzzleManiaLevels from '../static/data/puzzleManiaLevels.json';
import spaceFootballLevels from '../static/data/spaceFootballLevels.json';
import aimTrainCover from '../static/img/MadeByESL/coverAimTrain.avif';
import puzzleManiaCover from '../static/img/MadeByESL/coverPuzzleMania.avif';
import spaceFootballCover from '../static/img/MadeByESL/coverSpaceFootball.png.avif';
import firstTaskBG from '../static/img/DailyTasks/iconFirstTaskBG.svg';
import submitBtnBg from '../static/img/DailyTasks/iconSubmitEntryBtn.svg';
import dummyImg from '../static/img/Home/pubgDummy.avif';
import { isAuthenticated } from '../api/auth';
import MessagePopup from '../components/MessagePopup';
import { trackChallengeAction } from '../utils/analytics';
import playIcon from '../static/img/DailyGamingTasks/play.png';

const GameChallenges = () => {
  const [searchParams] = useSearchParams();
  const gameId = searchParams.get('gameId');
  const navigate = useNavigate();

  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [popupImageSrc, setPopupImageSrc] = useState(null);

  const [isSubmitPopupOpen, setIsSubmitPopupOpen] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState(null);

  const [messagePopupOpen, setMessagePopupOpen] = useState(false);
  const [messagePopupMsg, setMessagePopupMsg] = useState('');

  const handleImageClick = (imageUrl) => {
    setPopupImageSrc(imageUrl);
  };

  const handleClosePopup = () => {
    setPopupImageSrc(null);
    // console.log("Closing popup"); 
  };

  const handleOpenSubmitPopup = (challenge) => {
    if (!isAuthenticated()) {
      window.location.href = process.env.REACT_APP_LOGIN_URL;
      return;
    }
    setSelectedChallenge(challenge);
    setIsSubmitPopupOpen(true);
    trackChallengeAction(gameData?.game || 'Unknown', challenge.challengeId, 'submit');
  };

  const handleCloseSubmitPopup = () => {
    setIsSubmitPopupOpen(false);
    setSelectedChallenge(null);
  };

  // For Aim Train, handle Play Now button
  const handlePlayNow = async (challenge) => {
    if (!isAuthenticated()) {
      window.location.href = process.env.REACT_APP_LOGIN_URL;
      return;
    }
    const payload = {
      game_unique_id: 'aim_train_001',
      game_name: 'Aim Train',
      game_level: String(challenge.challengeId),
      level_credits: String(challenge.prize)
    };
    try {
      const resp = await submitGameEntry(payload);
      if (resp.success) {
        trackChallengeAction('Aim Train', challenge.challengeId, 'start');
        navigate('/aim-train', { state: payload });
      } else {
        setMessagePopupMsg(resp.message || 'Something went wrong.');
        setMessagePopupOpen(true);
      }
    } catch (e) {
      setMessagePopupMsg('An error occurred. Please try again.');
      setMessagePopupOpen(true);
    }
  };

  // For Puzzle Mania, handle Play Now button
  const handlePlayPuzzleMania = async (challenge) => {
    if (!isAuthenticated()) {
      window.location.href = process.env.REACT_APP_LOGIN_URL;
      return;
    }
    const payload = {
      game_unique_id: 'puzzle_mania_001',
      game_name: 'Puzzle Mania',
      game_level: String(challenge.challengeId),
      level_credits: String(challenge.prize)
    };
    try {
      const resp = await submitGameEntry(payload);
      if (resp.success) {
        trackChallengeAction('Puzzle Mania', challenge.challengeId, 'start');
        navigate('/puzzle-mania', { state: payload });
      } else {
        setMessagePopupMsg(resp.message || 'Something went wrong.');
        setMessagePopupOpen(true);
      }
    } catch (e) {
      setMessagePopupMsg('An error occurred. Please try again.');
      setMessagePopupOpen(true);
    }
  };

  // For Space Football, handle Play Now button
  const handlePlaySpaceFootball = async (challenge) => {
    if (!isAuthenticated()) {
      window.location.href = process.env.REACT_APP_LOGIN_URL;
      return;
    }
    const payload = {
      game_unique_id: 'space_football_001',
      game_name: 'Space Football',
      game_level: String(challenge.challengeId),
      level_credits: String(challenge.prize)
    };
    try {
      const resp = await submitGameEntry(payload);
      if (resp.success) {
        trackChallengeAction('Space Football', challenge.challengeId, 'start');
        navigate('/space-football', { state: payload });
      } else {
        setMessagePopupMsg(resp.message || 'Something went wrong.');
        setMessagePopupOpen(true);
      }
    } catch (e) {
      setMessagePopupMsg('An error occurred. Please try again.');
      setMessagePopupOpen(true);
    }
  };

  useEffect(() => {
    const loadChallenges = async () => {
      if (!gameId) {
        setError('No game selected. Please go back and select a game.');
        setLoading(false);
        return;
      }
      // If AimTrain, skip API and use static data
      if (gameId === 'aim_train_001') {
        setGameData({
          game: 'Aim Train',
          challenges: []
        });
        setLoading(false);
        return;
      }
      // If Puzzle Mania, skip API and use static data
      if (gameId === 'puzzle_mania_001') {
        setGameData({
          game: 'Puzzle Mania',
          challenges: []
        });
        setLoading(false);
        return;
      }
      // If Space Football, skip API and use static data
      if (gameId === 'space_football_001') {
        setGameData({
          game: 'Space Football',
          challenges: []
        });
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await fetchGameChallenges(gameId);
        if (data) {
          setGameData(data);
        } else {
          setError('Failed to load challenges for this game. Please try again later.');
        }
      } catch (err) {
        setError('An error occurred while fetching challenges.');
      } finally {
        setLoading(false);
      }
    };

    loadChallenges();
  }, [gameId]);

  useEffect(() => {
    document.title = 'Game Challenges | Jazz Esl';
  }, []);

  // If Aim Train, use levels from aimTrainLevels.json
  let filteredChallenges = gameData?.challenges
    ?.filter(challenge => challenge.archived === 0)
    ?.sort((a, b) => a.makeEntry - b.makeEntry) || [];

  const isAimTrain = gameId === 'aim_train_001';
  const isPuzzleMania = gameId === 'puzzle_mania_001';
  const isSpaceFootball = gameId === 'space_football_001';

  const eslGames = ['aim_train_001', 'puzzle_mania_001', 'space_football_001'];

  if (isAimTrain) {
    filteredChallenges = aimTrainLevels.map(level => ({
      challengeName: `Pass Level ${level.level}`,
      prize: level.reward,
      challengeThumbnail: aimTrainCover,
      makeEntry: 0,
      archived: 0,
      challengeId: level.level,
      id: level.level
    }));
  }

  if (isPuzzleMania) {
    filteredChallenges = puzzleManiaLevels.map(level => ({
      challengeName: `Play ${level.difficulty} Mode`,
      objective: level.description,
      prize: level.reward,
      challengeThumbnail: puzzleManiaCover,
      makeEntry: 0,
      archived: 0,
      challengeId: level.level,
      id: level.level
    }));
  }

  if (isSpaceFootball) {
    filteredChallenges = spaceFootballLevels.map(level => ({
      challengeName: `Beat Level ${level.level}`,
      objective: level.description,
      prize: level.reward,
      challengeThumbnail: spaceFootballCover,
      makeEntry: 0,
      archived: 0,
      challengeId: level.level,
      id: level.level
    }));
  }

  if (loading) {
    return <div className={styles.loadingState}>Loading challenges...</div>;
  }

  if (error) {
    return <div className={styles.errorState}>{error}</div>;
  }

  if (!gameData || filteredChallenges.length === 0) {
    const gameTitle = gameData?.game || 'Selected Game';
    return (
      <div className={styles.container}>
        <h2 className={styles.heading}>{gameTitle} Daily Tasks</h2>
        <p className={styles.subheading}>Play daily tasks & win extra credits</p>
        <div className={styles.emptyState}>No challenges available for this game right now.</div>
      </div>
    );
  }

  const gradients = [
    "rgba(255, 255, 255, 0.04)",
  ];

  const { game } = gameData;

  return (
    <div className={styles.container}>
      <div className={styles.navigationHeader}>
        <div
          className={styles.navigationBack}
          onClick={() => navigate(-1)}
        >
          <img src={ArrowLeft} alt="<" />
        </div>
        <div className={styles.navigationTitle}>
          <p>{game}</p>
          <h2 className={styles.heading}>Gaming Challenges</h2>
        </div>
      </div>
      <div className={styles.gamingTaskHeader}>
        <p className={styles.subheading}>Play - compete - earn</p>
        <h2 className={styles.heading}>pick any {game} task</h2>
        <p className={styles.subheading}>win credits</p>
      </div>


      {filteredChallenges.length > 0 && (
        <div className={styles.cardGrid}>
          {filteredChallenges.map((challenge, index) => {
            let imageUrl = challenge.challengeThumbnail;

            if (!eslGames.includes(gameId) && imageUrl) {
              if (
                !imageUrl.startsWith("data:") &&
                !imageUrl.startsWith("http")
              ) {
                imageUrl = `data:image/jpeg;base64,${imageUrl}`;
              }
            }

            const isSubmitted = challenge.makeEntry === 1;

            return (
              <div
                key={challenge.id || index}
                className={styles.card}
                style={{
                  "--bg": gradients[index % gradients.length],
                }}
                onClick={() => handleImageClick(imageUrl)}
              >
                <div className={styles.cardImage}>
                  <img
                    src={imageUrl || dummyImg}
                    alt={challenge.challengeName || "Challenge"}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = dummyImg;
                    }}
                  />
                </div>

                {/* INFO */}
                <div className={styles.cardGameInfo}>
                  <span>
                    {challenge.challengeName || "Challenge Name"}
                  </span>
                  <h2>
                    Win {challenge.prize || "Reward Info"} Credits
                  </h2>
                </div>

                {/* ACTION */}
                {/* ACTION */}
                <div className={styles.cardGamePlay}>
                  {isSubmitted ? (
                    <div className={styles.claimed}>SUBMITTED</div>
                  ) : isAimTrain ? (
                    <button
                      className={styles.playButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlayNow(challenge);
                      }}
                    >
                      Play Now
                    </button>
                  ) : isPuzzleMania ? (
                    <button
                      className={styles.playButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlayPuzzleMania(challenge);
                      }}
                    >
                      Play Now
                    </button>
                  ) : isSpaceFootball ? (
                    <button
                      className={styles.playButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlaySpaceFootball(challenge);
                      }}
                    >
                      Play Now
                    </button>
                  ) : (
                    <button
                      className={styles.playButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenSubmitPopup(challenge);
                      }}
                    >
                      Submit Entry
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {popupImageSrc && (
        <div className={styles.imagePopupOverlay} onClick={handleClosePopup}>
          <div className={styles.imagePopupContent} onClick={(e) => e.stopPropagation()}>
            <img src={popupImageSrc} alt="Challenge Preview" />
            <button className={styles.closePopupButton} onClick={handleClosePopup}>&times;</button>
          </div>
        </div>
      )}

      {isSubmitPopupOpen && selectedChallenge && (
        <GamingTaskSubmitPopup
          isOpen={isSubmitPopupOpen}
          onClose={handleCloseSubmitPopup}
          gameId={gameId}
          challengeId={selectedChallenge.challengeId}
          challengeName={selectedChallenge.challengeName}
          challengeObjective={selectedChallenge.objective}
          taskImage={(() => {
            let taskImage = selectedChallenge.challengeThumbnail;
            // Only format base64 for non-ESL games
            if (!eslGames.includes(gameId) && taskImage) {
              if (!taskImage.startsWith('data:') && !taskImage.startsWith('http')) {
                taskImage = `data:image/jpeg;base64,${taskImage}`;
              }
            }
            return taskImage;
          })()}
        />
      )}
      <MessagePopup
        isOpen={messagePopupOpen}
        message={messagePopupMsg}
        onClose={() => setMessagePopupOpen(false)}
      />
    </div>
  );
};

export default GameChallenges;